import React, { createContext, useContext, useState } from 'react'

import type { BaseTableEntry } from '@/pages/App/Artifacts/Model/Table/model-table-entry'
import {
	RunWorkQueueItemState,
	RunWorkQueueItemType,
} from '@asta/run-artifacts/src/work-queue/work-queue'
import { v4 as uuid } from 'uuid'

export type WorkQueueItemDTO = {
	id?: string
	options: {
		sync: boolean
		test: boolean
	}
	type: string
	state?: string
	subItems?: BaseTableEntry[]
	pageId: string | null
	componentId: string | null
	activityId: string | null
	label: string
	strategies: any[]
}

type WorkQueueContextType = {
	workQueue: WorkQueueItemDTO[]
	addItemToWorkQueue: (item: WorkQueueItemDTO) => void
	removeItemFromWorkQueue: (index: number) => void
	clearWorkQueue: () => void
	addComponentToWorkQueue: (item: WorkQueueItemDTO) => void
}

const WorkQueueContext = createContext<WorkQueueContextType | undefined>(
	undefined
)

export const WorkQueueProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [workQueue, setWorkQueue] = useState<WorkQueueItemDTO[]>([])

	const addItemToWorkQueue = (item: WorkQueueItemDTO) => {
		setWorkQueue(prevItems => [...prevItems, item])
	}

	const removeItemFromWorkQueue = (index: number) => {
		setWorkQueue(prevItems => prevItems.filter((_, i) => i !== index))
	}

	const clearWorkQueue = () => {
		setWorkQueue([])
	}

	const addComponentToWorkQueue = (item: WorkQueueItemDTO) => {
		// Check if it's a page component with subitems
		if (
			item.type === RunWorkQueueItemType.Page &&
			item.subItems?.length > 0
		) {
			const newItem = {
				...item,
				id: item.id || uuid(),
				state: item.state || RunWorkQueueItemState.Todo,
			}
			addItemToWorkQueue(newItem)
		} else {
			// For regular components or pages without subitems
			const newItem = {
				...item,
				id: item.id || uuid(),
				state: item.state || RunWorkQueueItemState.Todo,
			}
			addItemToWorkQueue(newItem)
		}
	}

	const value = {
		workQueue,
		addItemToWorkQueue,
		removeItemFromWorkQueue,
		clearWorkQueue,
		addComponentToWorkQueue,
	}

	return (
		<WorkQueueContext.Provider value={value}>
			{children}
		</WorkQueueContext.Provider>
	)
}

export const useWorkQueue = (): WorkQueueContextType => {
	const context = useContext(WorkQueueContext)
	if (context === undefined) {
		throw new Error('useWorkQueue must be used within a WorkQueueProvider')
	}
	return context
}
